import { createAsyncThunk } from '@reduxjs/toolkit'
import { canWrite } from '@tunasong/models'
import type { Entity, Persisted } from '@tunasong/schemas'
import { entitiesApi } from '../../api/entities.js'
import type { RootState } from '../../configure-store.js'
import type { TunaThunkDispatch } from '../../hooks/thunk-dispatch.hook.js'
import invariant from 'tiny-invariant'

export const addChild = createAsyncThunk<
  Persisted<Entity>,
  { parentId: string; childId: string },
  { state: RootState; dispatch: TunaThunkDispatch }
>(
  'entities/addChild',
  async ({ parentId: providedParentId, childId }, { dispatch, getState }): Promise<Persisted<Entity>> => {
    const { userId } = getState().user

    invariant(userId, 'User must be logged in to add a child entity')

    const parentId = providedParentId === 'ROOT' ? null : providedParentId

    const parent = parentId ? entitiesApi.endpoints.loadEntity.select({ id: parentId })(getState())?.data : null
    const child = entitiesApi.endpoints.loadEntity.select({ id: childId })(getState())?.data
    const childETag = getState().entities.eTags[childId]

    if (!child || (parentId && !parent)) {
      throw new Error(`Entity ${childId} not found - this is unexpected`)
    }

    const canWriteChild = userId === child.userId || canWrite(userId, child.acls)
    const canWriteParent = !parent || userId === parent.userId || canWrite(userId, parent.acls ?? [])

    if (!(canWriteChild && canWriteParent)) {
      throw new Error(`User ${userId} does not have permission add child ${childId} to parent ${parentId}`)
    }

    /**
     * One PATCH moves the entity. The server checks the destination and re-derives the child's
     * ACLs from it (GH #1958); the client used to compute them here and call `share` afterwards.
     */
    return dispatch(
      entitiesApi.endpoints.updateEntity.initiate({
        id: child.id,
        partialEntity: { parentId: parentId ?? 'ROOT' },
        eTag: childETag,
      })
    ).unwrap()
  }
)
